import { endOfDay, max, min, startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
	reduce,
	get,
	keyBy,
	filter,
	flatten,
	flow,
	groupBy,
	isEmpty,
	isNil,
	map,
	mapValues,
	orderBy,
	property,
	reject,
	take,
	thru,
	min as lodashMin,
	some,
	find,
	isEqual as lodashIsEqual,
	uniqBy,
	concat
} from 'lodash-es';
import { Frequency, RRule } from 'rrule';
import { EventDto, ScheduleRepeatDto } from "../models/dto";
import { ScheduleRepeatTypesEnum } from "../enums";
import { EventInstanceSortEnum, EventPeriodFilter } from "../models/filters";
import { compact } from 'lodash-es';

export type EventRRuleInstance = {
	id: string;
	baseId: string;
	event: EventDto;
	authorId: string;
	rrule: RRule | null;
	instance: Date;
	nextRRuleParams?: { dt: Date, inc?: boolean };
	distance?: number | null;
};

export type EventRRuleOptions = {
	recurringEvents: EventDto[],
	maxEventsPerAuthorPerDay?: number,
	singleEvents: EventDto[],
	lastEventId?: string,
	minDate: Date,
	maxDate?: Date,
	minDistance?: number,
	periods: EventPeriodFilter[],
	limit: number,
	sort: EventInstanceSortEnum,
	oneInstancePerEvent?: boolean,
};

export const convertScheduleRepeatToRRule = (data: ScheduleRepeatDto): string | null => {
	switch (data.typeId) {
		case ScheduleRepeatTypesEnum.Never:
			return null;
		case ScheduleRepeatTypesEnum.Daily:
			return new RRule({
				freq: Frequency.DAILY,
				interval: data.interval || 1,
			}).toString();
		case ScheduleRepeatTypesEnum.Weekly:
			return new RRule({
				freq: Frequency.WEEKLY,
				interval: data.interval || 1,
				byweekday: data.daysOfWeek?.map(({ dayNumber }) =>
					[RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA][dayNumber])
			}).toString();
		case ScheduleRepeatTypesEnum.Monthly:
			return new RRule(data.weekOfMonth ? {
				freq: Frequency.MONTHLY,
				bysetpos: data.weekOfMonth,
				byweekday: data.daysOfWeek?.map(({ dayNumber }) =>
					[RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA][dayNumber])
			} : {
				freq: Frequency.MONTHLY,
				interval: data.interval || 1,
				bymonthday: data.dayOfMonth || [],
			}).toString();
		default:
			throw new Error('unknown ScheduleRepeat type');
	}
};

export const getEventsRRuleInstances = (options: EventRRuleOptions): EventRRuleInstance[] => {
	const { recurringEvents, singleEvents, sort, maxEventsPerAuthorPerDay } = options;

	if (maxEventsPerAuthorPerDay && sort === 'instance') {
		const instances = getEventsRRuleInstances(options.maxDate ? {
			...options,
			maxEventsPerAuthorPerDay: undefined,
			minDate: startOfDay(options.minDate),
			maxDate: endOfDay(options.maxDate),
		} : {
			...options,
			maxEventsPerAuthorPerDay: undefined,
			minDate: startOfDay(options.minDate),
			limit: options.limit * 5,
		});

		const groupedInstances = groupBy(instances, ({ instance, authorId }) => JSON.stringify({ instance: startOfDay(instance), authorId }));
		const mappedInstances = map(groupedInstances, group => {
			const ordered = orderBy(group, [
				flow([property('event.prices'), isEmpty]), // NULLS FIRST
				flow([property('event.prices'), (group) => map(group, property('priceAmount')), lodashMin]), // main sort column
				'id', // additional sort column
			], [
				'desc',
				'asc',
				'asc',
			]);

			return take(ordered, maxEventsPerAuthorPerDay)
		});

		return thru(
			flatten(mappedInstances) as EventRRuleInstance[],
			(limitedInstances: EventRRuleInstance[]) => orderAndLimitEventInstances(limitedInstances, options)
		);
	}

	const recurringEventsMap: Record<string, EventDto> = keyBy(recurringEvents, 'id');
	const singleEventsMap: Record<string, EventDto> = keyBy(singleEvents, 'id');
	const rrulesMap: Record<string, RRule> = mapValues(recurringEventsMap, (event: EventDto) => convertEventToRRule(event, options));

	const recurringEventsInstances: EventRRuleInstance[] = reduce(rrulesMap, (acc: EventRRuleInstance[], rrule: RRule, id: string) => {
		const event = get(recurringEventsMap, id);
		const instance = getNextEventRRuleInstance({
			id,
			baseId: event?.baseId,
			event,
			authorId: event?.authorId,
			rrule,
			distance: event?.distance
		}, options);

		if (instance) {
			acc.push(instance);
		}

		return acc;
	}, []);

	const filteredInstances = filter(singleEventsMap, (event: EventDto) => {
		const { actualFrom, actualTo, startDate } = event;
		const _actualFrom = new Date(actualFrom).getTime();
		const _actualTo = actualTo ? new Date(actualTo).getTime() : Number.MAX_SAFE_INTEGER;
		const _startDate = new Date(startDate).getTime();

		return _actualFrom <= _startDate && _startDate < _actualTo;
	});

	const singleEventsInstances: EventRRuleInstance[] = map(filteredInstances, (event: EventDto) => ({
		id: event.id,
		baseId: event.baseId,
		event,
		authorId: event?.authorId,
		rrule: null,
		instance: event.startDate,
		distance: event.distance,
	}));

	const nextInstancesCalculationNeeded = sort === 'instance' && !options.oneInstancePerEvent;

	let allInstances = orderAndLimitEventInstances(
		[...recurringEventsInstances, ...singleEventsInstances],
		options,
		nextInstancesCalculationNeeded,
	);

	// other sorting modes don't support recurring events because of the infinite repeating of the first event
	if (nextInstancesCalculationNeeded) {
		allInstances = calculateNextEventsRRuleInstances(allInstances, options);
	}

	return allInstances;
}

// https://www.npmjs.com/package/rrule - Important: Use UTC dates
const convertRRuleDateToRegularDate = (rruleDate: Date): Date => {
	const date = new Date(rruleDate);
	date.setFullYear(rruleDate.getUTCFullYear());
	date.setMonth(rruleDate.getUTCMonth());
	date.setDate(rruleDate.getUTCDate());
	date.setHours(rruleDate.getUTCHours());
	date.setMinutes(rruleDate.getUTCMinutes());

	return date;
}

// https://www.npmjs.com/package/rrule - Important: Use UTC dates
const convertRegularDateToRRuleDate = (date: Date, tzid?: string): Date => {
	const zonedDate = tzid
		? utcToZonedTime(new Date(date), tzid)
		: date;
	const rruleDate = new Date(zonedDate);
	rruleDate.setUTCFullYear(zonedDate.getFullYear());
	rruleDate.setUTCMonth(zonedDate.getMonth());
	rruleDate.setUTCDate(zonedDate.getDate());
	rruleDate.setUTCHours(zonedDate.getHours());
	rruleDate.setUTCMinutes(zonedDate.getMinutes());

	return rruleDate;
}

const convertEventToRRule = (event: EventDto, options: EventRRuleOptions): RRule => {
	const { maxDate } = options;
	const { actualTo, startDate } = event;
	const rrule = RRule.fromString(event.isSingleEvent ? '' : event.rrule || '');
	const eventEnd = event.scheduleRepeat?.endDate ?? null;
	const dtstart = new Date(startDate);
	const until = eventEnd || maxDate || actualTo
		? min(compact([eventEnd, maxDate, actualTo]))
		: null;

	rrule._cache = null;
	rrule.options = {
		...rrule.options,
		dtstart: convertRegularDateToRRuleDate(dtstart, event.tzid),
		until: until && convertRegularDateToRRuleDate(until, event.tzid),
		bymonthday: rrule.options.bymonthday?.map(monthday => {
			const date = new Date(startDate);
			date.setUTCDate(monthday);

			return utcToZonedTime(date, event.tzid).getDate();
		}),
		byweekday: rrule.options.byweekday?.map(weekday => {
			const shiftValue = new Date(startDate).getUTCDay() - utcToZonedTime(new Date(startDate), event.tzid).getDay();

			return (weekday - shiftValue + 7) % 7;
		}),
		byhour: [utcToZonedTime(startDate, event.tzid).getHours()],
		byminute: [utcToZonedTime(startDate, event.tzid).getMinutes()],
		bysecond: [utcToZonedTime(startDate, event.tzid).getSeconds()],
		tzid: event.tzid,
	};

	return rrule;
};

const getNextEventRRuleInstance = (
	instance: Omit<EventRRuleInstance, 'instance'>,
	options: EventRRuleOptions
): EventRRuleInstance | undefined => {
	const nextInstanceDate = getNextRRuleInstance(instance, options);

	if (nextInstanceDate) {
		return { ...instance, instance: nextInstanceDate };
	}

	return;
}

const getNextRRuleInstance = (eventRRuleInstance: Omit<EventRRuleInstance, 'instance'>, options: EventRRuleOptions): Date | undefined => {
	if (!eventRRuleInstance.rrule) {
		return;
	}

	const nextRRuleParams = eventRRuleInstance.nextRRuleParams ?? {
		dt: max([options.minDate, eventRRuleInstance.event.actualFrom]),
		inc: true
	};
	const rruleInstance = eventRRuleInstance.rrule.after(
		convertRegularDateToRRuleDate(nextRRuleParams.dt),
		nextRRuleParams.inc,
	);

	const instance = rruleInstance && convertRRuleDateToRegularDate(rruleInstance);

	if (instance && !checkIsInstanceInPeriods(instance, options)) {
		const nearestPeriodStart = getNearestPeriodStart(instance, options);

		if (!nearestPeriodStart) {
			return;
		}

		// go to the nearest period and find the next instance
		eventRRuleInstance.nextRRuleParams = { dt: nearestPeriodStart, inc: true } // rrule.after(nearestPeriodStart, true)

		return getNextRRuleInstance(eventRRuleInstance, options);
	}

	if (instance) {
		eventRRuleInstance.nextRRuleParams = { dt: instance, inc: false } // rrule.after(instance, false)
	}

	return instance ?? undefined;
};

const checkIsInstanceInPeriods = (instance: Date, { periods }: EventRRuleOptions): boolean => some(
	periods,
	([from, to]) => {
		const instanceTime = new Date(instance).getTime();
		const fromTime = new Date(from).getTime();
		const toTime = to ? new Date(to).getTime() : 0;

		return instanceTime >= fromTime && (!toTime || toTime >= instanceTime);
	}
);

const getNearestPeriodStart = (date: Date, { periods }: EventRRuleOptions): Date | undefined => find(
	periods,
	(([from]) => from.getTime() > new Date(date).getTime())
)?.[0];

const orderAndLimitEventInstances = (results: EventRRuleInstance[], options: EventRRuleOptions, ignoreLastEventIdCheck = false): EventRRuleInstance[] => {
	const { minDate, lastEventId, limit, sort, minDistance } = options;

	let instancesToReturn = orderBy(results,
		[
			flow([property(sort), isNil]), // NULLS LAST
			sort, // main sort column
			'id' // additional sort column
		],
	);

	instancesToReturn = filter(instancesToReturn as EventRRuleInstance[], ({ instance }) => checkIsInstanceInPeriods(instance, options))
	instancesToReturn = reject(instancesToReturn as EventRRuleInstance[], (eventInstance: EventRRuleInstance) => {
			if (ignoreLastEventIdCheck) return false;

			const { distance, instance, id } = eventInstance;

			if (sort === 'instance') {
				/**
				 * if several instances have the same date, they should be filtered by sorted "id"
				 *
				 * Example:
				 * - ...
				 * - 2022-02-24T07:30:00.000Z id=00000000-0000-0000-0000-000000000500
				 * - 2022-02-24T07:30:00.000Z id=00000000-0000-0000-0000-000000000501
				 * - 2022-02-24T07:30:00.000Z id=00000000-0000-0000-0000-000000000502
				 * - 2022-02-24T08:30:00.000Z id=00000000-0000-0000-0000-000000000400
				 * - 2022-02-24T09:30:00.000Z id=00000000-0000-0000-0000-000000000900 //!! id < lastEventId
				 * - 2022-02-24T09:30:00.000Z id=00000000-0000-0000-0000-000000000901 //!! id = lastEventId
				 * ---------------------- end of first chunk ------------------------
				 * -- minDate=2022-02-24T09:30:00.000Z --
				 * -- lastEventId=00000000-0000-0000-0000-000000000901 --
				 * -------------------- start of the next chunk --------------------
				 * - 2022-02-24T09:30:00.000Z id=00000000-0000-0000-0000-000000000902 //!! id > lastEventId
				 * - 2022-02-24T09:30:00.000Z id=00000000-0000-0000-0000-000000000903 //!! id > lastEventId
				 * - 2022-02-24T10:30:00.000Z id=00000000-0000-0000-0000-000000000123
				 * - ...
				 *
				 * So if the date of a row is equal to "minDate", we should skip it if "id" <= "lastEventId"
				 */

				return new Date(instance).getTime() === new Date(minDate).getTime()
					&& !!lastEventId && id <= lastEventId
			} else {
				/**
				 * if several instances have the same date, they should be filtered by sorted "id"
				 *
				 * Example:
				 * - ...
				 * - distance=   10 id=00000000-0000-0000-0000-000000000500
				 * - distance=  100 id=00000000-0000-0000-0000-000000000501
				 * - distance= 1000 id=00000000-0000-0000-0000-000000000502
				 * - distance= 1000 id=00000000-0000-0000-0000-000000000400
				 * - distance= 2000 id=00000000-0000-0000-0000-000000000900 //!! id < lastEventId
				 * - distance= 2000 id=00000000-0000-0000-0000-000000000901 //!! id = lastEventId
				 * ---------------------- end of first chunk ------------------------
				 * -- minDistance=2000 --
				 * -- lastEventId=00000000-0000-0000-0000-000000000901 --
				 * -------------------- start of the next chunk --------------------
				 * - distance= 2000 id=00000000-0000-0000-0000-000000000902 //!! id > lastEventId
				 * - distance= 2000 id=00000000-0000-0000-0000-000000000903 //!! id > lastEventId
				 * - distance=10000 id=00000000-0000-0000-0000-000000000123
				 * - ...
				 *
				 * So if the distance of a row is equal to "minDistance", we should skip it if "id" <= "lastEventId"
				 */

				return !!distance && !!minDistance && minDistance === distance
					&& !!lastEventId && id <= lastEventId
			}
		});

	if (limit) {
		instancesToReturn = take(instancesToReturn, limit);
	}

	return instancesToReturn as EventRRuleInstance[];
}

const calculateNextEventsRRuleInstances = (
	prevInstances: EventRRuleInstance[],
	options: EventRRuleOptions,
): EventRRuleInstance[] => {

	let newInstances = uniqBy(prevInstances, 'id');
	newInstances = reduce(newInstances, (acc: EventRRuleInstance[], instance: EventRRuleInstance) => {
		const nextInstance = getNextEventRRuleInstance(instance, options);

		if (nextInstance) {
			acc.push(nextInstance);
		}

		return acc;
	}, []) // new instances
	newInstances = concat(newInstances, prevInstances);
	newInstances = thru(newInstances, (instances) => orderAndLimitEventInstances(instances, options));

	if (!lodashIsEqual(prevInstances, newInstances)) {
		return calculateNextEventsRRuleInstances(newInstances, options);
	}

	return prevInstances;
};

export const calcInstancesBetweenDates = (rrule: RRule, after: Date, before: Date, inc = true, maxInstances = 10): Date[] => {
	const iterator = (date: Date, len: number) => {
		if (len > maxInstances) return false;

		return checkIsInstanceInPeriods(convertRRuleDateToRegularDate(date), {
			periods: [[after, before]],
		} as any);
	};

	const instances = rrule.between(convertRegularDateToRRuleDate(after), convertRegularDateToRRuleDate(before), inc, iterator);

	return instances.map(convertRRuleDateToRegularDate);
};

export const getInstancesForEventBetweenDates = (event: EventDto, after: Date, before: Date, inc = true, maxInstances = 10): Date[] => {
	const rrule = convertEventToRRule(event, {
		maxDate: before,
	} as any);

	if (!rrule) return [event.startDate];

	return calcInstancesBetweenDates(rrule, after, before, inc, maxInstances);
};
